import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

import conversationService from '../../services/conversationService';

interface AudioPlayerProps {
  conversationId: string;
  messageId: string;
  duration?: number;
  onError?: (error: string) => void;
  variant?: 'default' | 'chat';
  tone?: 'inbound' | 'outbound';
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  conversationId,
  messageId,
  duration,
  onError,
  variant = 'default',
  tone = 'inbound',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [volume, setVolume] = useState(1);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    loadAudioMessage();
  }, [conversationId, messageId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setAudioDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setError('Error al cargar el audio');
      setIsPlaying(false);
      onError?.('Error al cargar el audio');
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, onError]);

  const loadAudioMessage = async () => {
    setLoading(true);
    setError(null);

    try {
      const audioData = await conversationService.getAudioMessage(conversationId, messageId);
      
      // In a real implementation, you would get the actual audio URL
      // For now, we'll simulate with a placeholder
      if (audioData.download_url) {
        setAudioUrl(audioData.download_url);
      } else {
        setError('Audio no disponible');
      }
      
      if (audioData.audio_duration) {
        setAudioDuration(audioData.audio_duration);
      }
    } catch (err) {
      setError('Error al cargar el mensaje de audio');
      onError?.('Error al cargar el mensaje de audio');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {
        setError('Error al reproducir el audio');
        onError?.('Error al reproducir el audio');
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const time = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const vol = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.volume = vol;
    setVolume(vol);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const isChatVariant = variant === 'chat';
  const palette = isChatVariant
    ? {
        background: tone === 'outbound' ? 'rgba(0, 168, 132, 0.08)' : 'rgba(17, 27, 33, 0.04)',
        border: 'transparent',
        accent: tone === 'outbound' ? '#008f72' : '#54656f',
        accentSoft: tone === 'outbound' ? 'rgba(0, 168, 132, 0.14)' : 'rgba(84, 101, 111, 0.12)',
        text: '#111b21',
        meta: '#667781',
      }
    : {
        background: 'background.paper',
        border: 'divider',
        accent: 'primary.main',
        accentSoft: 'rgba(25, 118, 210, 0.1)',
        text: 'text.primary',
        meta: 'text.secondary',
      };

  const handleDownload = async () => {
    try {
      const audioData = await conversationService.getAudioMessage(conversationId, messageId);
      if (audioData.download_url) {
        const link = document.createElement('a');
        link.href = audioData.download_url;
        link.download = `audio_${messageId}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      onError?.('Error al descargar el audio');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        p={isChatVariant ? 0.5 : 1}
        sx={{ color: palette.meta }}
      >
        <CircularProgress size={20} />
        <Typography variant="caption">Cargando audio...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={isChatVariant ? 0.5 : 1}>
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      p={isChatVariant ? 1.25 : 2}
      bgcolor={palette.background}
      borderRadius={isChatVariant ? 3 : 2}
      border={1}
      borderColor={palette.border}
      minWidth={isChatVariant ? 0 : 300}
      width={isChatVariant ? '100%' : 'auto'}
    >
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      )}
      
      <Tooltip title={isPlaying ? 'Pausar' : 'Reproducir'}>
        <IconButton
          onClick={togglePlayPause}
          disabled={!audioUrl}
          size="small"
          sx={{
            bgcolor: palette.accentSoft,
            color: palette.accent,
            '&:hover': {
              bgcolor: palette.accentSoft,
            },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
      </Tooltip>

      <Box flex={1} mx={1}>
        {isChatVariant && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.5,
              color: palette.meta,
              fontWeight: 600,
            }}
          >
            Mensaje de audio
          </Typography>
        )}
        <Slider
          value={currentTime}
          max={audioDuration}
          onChange={handleSeek}
          disabled={!audioUrl}
          size="small"
          sx={{
            height: 4,
            color: palette.accent,
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
            },
          }}
        />
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          <Typography variant="caption" sx={{ color: palette.meta }}>
            {formatTime(currentTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: palette.meta }}>
            {formatTime(audioDuration)}
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        minWidth={isChatVariant ? 64 : 80}
        sx={{ color: palette.meta }}
      >
        <VolumeIcon fontSize="small" />
        <Slider
          value={volume}
          min={0}
          max={1}
          step={0.1}
          onChange={handleVolumeChange}
          size="small"
          sx={{
            width: isChatVariant ? 40 : 50,
            color: palette.accent,
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
            },
          }}
        />
      </Box>

      <Tooltip title="Descargar audio">
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            color: palette.meta,
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AudioPlayer;
